import React, { useState, useEffect, useRef } from 'react';
import '../styles/AboutPage.css';
import { AuthModal } from '../components/AuthModal';
import { aboutService } from '../services/aboutService';
import { sanitizeHtmlForRender } from '../utils/sanitizeHtml';
import { Helmet, HelmetProvider } from 'react-helmet-async';

// Animated counter hook
function useAnimatedCounter(end, duration = 2000, start = 0) {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * (end - start) + start));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration, start]);

  return { count, ref };
}

// Animated stat component
function AnimatedStat({ end, suffix, label }) {
  const { count, ref } = useAnimatedCounter(end, 2000);

  return (
    <div className="stat-item" ref={ref}>
      <span className="stat-number">{count}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

export function AboutPage() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

  const fallbackContent = {
    hero_image: '/assets/aboutp.jpeg',
    role: 'Certified Fitness Coach',
    title_line1: "Hey I'm",
    title_highlight: 'Daniella',
    tagline: "I'm here to prove that fitness doesn't have to feel intimidating or impossible.",
    intro: "Like many people, my journey into fitness wasn't perfect or predictable. But along the way, I discovered something powerful: when you find an approach that works for you, fitness becomes something you actually enjoy.",
    story_title: 'About',
    story_body: "What truly defines my coaching style is the empathy and care I bring to understanding each client's needs. This allows us to build a plan that works for your body, your routine, and your lifestyle.<br/><br/>My programs follow a personalised approach that is effective, realistic, and sustainable. Each one is thoughtfully designed to fit into your life, helping you move forward with clarity, confidence, and consistency.",
    philosophy_title: 'My Philosophy',
    philosophy_body: "Fitness should feel empowering, not exhausting.<br/><br/>I believe the best results come from building sustainable habits and learning to enjoy the process. There's no \"perfect\" routine -- only the one that works for your body and your life.<br/><br/>My goal is to support you every step of the way -- celebrating your progress, helping you overcome challenges, and reminding you of how far you've come.<br/><br/>Because when fitness becomes part of your lifestyle rather than a short-term goal, the results last.",
    cta_title: "Let's make your goals a reality!",
    cta_text: "Ready to start your fitness journey? I'm here to make fitness fun, simple and sustainable for YOU!",
    cta_button_text: 'All Programs',
    cta_button_link: '/programs'
  };

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const data = await aboutService.getAbout();
        setContent(data || null);
      } catch (error) {
        console.error('Failed to load about page content', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  const data = content || fallbackContent;

  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  };

  const getImageUrl = (path) => {
    if (!path) return '/assets/aboutp.jpeg';
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  const renderHTML = (html) => ({ __html: sanitizeHtmlForRender(html || '') });

  return (
    <div className="about-page">
      <HelmetProvider>
        <Helmet>
          <title>
            About Daniella - Get Fit with Daniella
          </title>

          <meta
            name="description"
            content="Learn about Daniella and her approach to making fitness fun, sustainable, and achievable."
          />

          <link rel="canonical" href="https://getfitwithdaniella.com/about/" />

          {/* Open Graph */}
          <meta
            property="og:title"
            content="About Daniella - Transform Your Fitness Journey"
          />

          <meta
            property="og:description"
            content="Learn about Daniella and her approach to making fitness fun, sustainable, and achievable."
          />

          <meta
            property="og:url"
            content="https://getfitwithdaniella.com/about/"
          />

          <meta property="og:type" content="website" />

          <meta
            property="og:site_name"
            content="Get Fit with Daniella"
          />

          <meta
            property="og:image"
            content="https://getfitwithdaniella.com/assets/og-image.jpg"
          />

          {/* Twitter / X */}
          <meta
            name="twitter:card"
            content="summary_large_image"
          />

          <meta
            name="twitter:title"
            content="About Daniella - Get Fit with Daniella"
          />

          <meta
            name="twitter:description"
            content="Learn about Daniella and her approach to making fitness fun, sustainable, and achievable."
          />

          <meta
            name="twitter:image"
            content="https://www.getfitwithdaniella.com/assets/og-image.jpg"
          />
        </Helmet>
      </HelmetProvider>
      {loading && (
        <div className="about-loading">
          <div className="spinner" />
        </div>
      )}
      {/* Hero Section - Coach Profile Style */}
      <section className="about-hero-v2">
        <div className="about-hero-container-v2">
          <div className="about-hero-image-v2">
            <img src={getImageUrl(data.hero_image)} alt="Daniella Gomes - Fitness Coach" />
          </div>

          <div className="about-hero-content-v2">
            <span className="about-role">{data.role}</span>
            <h1 className="about-title-v2">
              {data.title_line1}<br />
              <span className="gradient-text">{data.title_highlight}</span>
            </h1>
            <p className="about-tagline-v2" dangerouslySetInnerHTML={renderHTML(data.tagline)} />
            <p className="about-intro" dangerouslySetInnerHTML={renderHTML(data.intro)} />
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <div className="about-container">
          <div className="story-content">
            <h2>{data.story_title}</h2>
            <p className="story-text" dangerouslySetInnerHTML={renderHTML(data.story_body)} />
          </div>

          <div className="story-content">
            <h2>{data.philosophy_title}</h2>
            <p className="story-text" dangerouslySetInnerHTML={renderHTML(data.philosophy_body)} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-container">
          <div className="cta-content">
            <h2>{data.cta_title}</h2>
            <p dangerouslySetInnerHTML={renderHTML(data.cta_text)} />
            <a href={data.cta_button_link || '/programs'} className="btn btn-primary btn-large about-cta-programs">
              {data.cta_button_text || 'All Programs'}
            </a>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultTab={authModalTab}
      />
    </div>
  );
}
